import { memo, useCallback, type JSX } from 'react';

import styled from '@mint-lib/styled';

import TabContainer from './TabContainer.jsx';

export const DRAWER_TABS_WIDTH = 48;

type Props<T> = {
  children: JSX.Element[];
  value: T;
  onChange: (value: T) => void;
};

function DrawerTabs<T>({ children, onChange, value }: Props<T>) {
  const handleChange = useCallback(
    (v: T) => {
      if (v === value) {
        return;
      }

      onChange(v);
    },
    [onChange],
  );

  return (
    <Wrapper role="tabpanel">
      {children.map((tab) => (
        <TabContainer
          key={tab.props.value}
          value={tab.props.value}
          isActive={value === tab.props.value}
          onChange={handleChange}
        >
          {tab}
        </TabContainer>
      ))}
    </Wrapper>
  );
}

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  background-color: ${({ theme }) => theme.palette.ui.primary};
  border: 1px solid ${({ theme }) => theme.palette.ui.tertiary};
  border-top: 0;
  border-right: 1px solid transparent;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(0.5)}`};
  width: ${DRAWER_TABS_WIDTH}px;
  height: 100%;
  z-index: 1;
`;

export default memo(DrawerTabs) as typeof DrawerTabs;
